/**
 * WalletOnboarding — the mandatory ~2–3 minute setup for a new Trust
 * Passport. Rebuilt for the "first-time user journey" spec:
 *
 *   1. Device lands on /wallet with no state → we redirect here.
 *   2. NAVI's intro video plays (auto-triggered by <NaviIntroModal />
 *      because it renders in the tree and no `navi_intro_seen_at`
 *      timestamp exists on a fresh device).
 *   3. Consumer walks through 6 short screens:
 *        step 0  Welcome / language confirm
 *        step 1  Name (first data-entry — "Save & exit" appears here)
 *        step 2  Cellphone number
 *        step 3  SA ID / passport number
 *        step 4  Selfie for face-match (simulated in this env)
 *        step 5  Consent + create Trust Wallet
 *   4. On finish we set `setup_completed_at`, and land on /wallet
 *      with an amber "Continue setting up your Financial Profile" nudge
 *      offering the optional 5-question profile.
 *
 * Every field autosaves via `updateWallet`. If the consumer taps
 * "Save & exit" or closes the tab mid-flow, they resume at the
 * furthest step reached on next visit.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadWallet, updateWallet } from "@/lib/wallet-storage";
import { NAVY, GOLD, BG } from "@/components/wallet/WalletShell";
import { WALLET_LANGUAGES } from "@/components/wallet/LanguageSwitcher";
import NaviIntroModal from "@/components/wallet/NaviIntroModal";
import NaviFace from "@/components/wallet/NaviFace";
import { ShieldCheck, ArrowRight, ArrowLeft, Camera, IdCard, Phone, User, Languages, Check, Loader2, Save } from "lucide-react";

const STEPS = [
  { key: "language", title: "Choose your language",      body: "NAVI will speak and write in the language you're most comfortable in.", icon: Languages, requiresData: false },
  { key: "name",     title: "What should we call you?",  body: "Only stored on your device. NAVI will greet you by this name.",          icon: User,      requiresData: true  },
  { key: "cell",     title: "Your cellphone number",     body: "Used to reach you when a bank or advisor sends a quote.",                  icon: Phone,     requiresData: true  },
  { key: "idnum",    title: "Verify your identity",      body: "Your SA ID or passport number — anchors your Trust Wallet.",             icon: IdCard,    requiresData: true  },
  { key: "selfie",   title: "Selfie face-match",         body: "One quick photo confirms it's really you. We only store the match result.", icon: Camera,   requiresData: true  },
  { key: "consent",  title: "Almost done",                body: "You control every piece of data shared. Nothing goes to a bank without your explicit tap.", icon: ShieldCheck, requiresData: false },
];

const LANGUAGES = WALLET_LANGUAGES.map(l => ({ code: l.code, label: l.native }));

/** SA ID Luhn-style checksum. Accepts any 13-digit numeric string that
 *  passes the Modulus-10 check used by SA IDs; passports are 9 chars
 *  alphanumeric. We validate loosely and defer strict biometric checks
 *  to the (mocked) verification provider. */
function isValidSaIdOrPassport(v) {
  const s = (v || "").trim().replace(/\s/g, "");
  if (!s) return false;
  if (/^\d{13}$/.test(s)) {
    const digits = s.split("").map(Number);
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      const d = digits[i];
      sum += i % 2 === 0 ? d : (((d * 2) % 10) + Math.floor((d * 2) / 10));
    }
    return (10 - (sum % 10)) % 10 === digits[12];
  }
  return /^[A-Z0-9]{6,12}$/i.test(s);
}
function isValidSaCell(v) {
  const s = (v || "").replace(/\s/g, "");
  return /^(\+?27|0)[6-8][0-9]{8}$/.test(s);
}

export default function WalletOnboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [savedFlash, setSavedFlash] = useState(false);
  const [busy, setBusy] = useState(false);

  // Initial state — pull anything already persisted so we resume where the consumer left off.
  const wallet = loadWallet() || {};
  const setup = wallet.setup || {};
  const initialLang = wallet.language || wallet.accessibility?.language || setup.language || "en";

  const [language, setLanguage] = useState(initialLang);
  const [name, setName]         = useState(setup.name       || wallet.name || "");
  const [cell, setCell]         = useState(setup.cell       || "");
  const [idnum, setIdnum]       = useState(setup.idnum      || "");
  const [selfie, setSelfie]     = useState(setup.selfie_captured_at ? { captured_at: setup.selfie_captured_at } : null);
  const [consent, setConsent]   = useState(Boolean(setup.consent));

  // Resume on the furthest incomplete step
  useEffect(() => {
    if (setup.name && !setup.cell)     setStep(2);
    else if (setup.cell && !setup.idnum)     setStep(3);
    else if (setup.idnum && !setup.selfie_captured_at) setStep(4);
    else if (setup.selfie_captured_at && !setup.consent)     setStep(5);
    else if (setup.name && !setup.completed_at) setStep(1);   // resume from name
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistPatch = (patch) => {
    const w = loadWallet() || {};
    const nextSetup = { ...(w.setup || {}), ...patch, updated_at: new Date().toISOString() };
    updateWallet({ setup: nextSetup, ...(patch.name ? { name: patch.name } : {}), ...(patch.language ? { language: patch.language, accessibility: { ...(w.accessibility || {}), language: patch.language } } : {}) });
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1400);
  };

  const cur = STEPS[step];
  const total = STEPS.length;
  const progressPct = Math.round(((step + 1) / total) * 100);
  const minutesLeft = Math.max(1, Math.ceil(((total - step - 1) * 25) / 60));

  const canContinue = useMemo(() => {
    switch (cur.key) {
      case "language": return Boolean(language);
      case "name":     return name.trim().length >= 2;
      case "cell":     return isValidSaCell(cell);
      case "idnum":    return isValidSaIdOrPassport(idnum);
      case "selfie":   return Boolean(selfie?.captured_at);
      case "consent":  return consent;
      default:         return false;
    }
  }, [cur.key, language, name, cell, idnum, selfie, consent]);

  const savePartial = () => {
    if (cur.key === "language") persistPatch({ language });
    if (cur.key === "name")     persistPatch({ name: name.trim() });
    if (cur.key === "cell")     persistPatch({ cell: cell.trim() });
    if (cur.key === "idnum")    persistPatch({ idnum: idnum.trim() });
    if (cur.key === "selfie" && selfie?.captured_at) persistPatch({ selfie_captured_at: selfie.captured_at });
  };

  const advance = () => {
    savePartial();
    if (step < total - 1) setStep(step + 1);
  };

  const finish = () => {
    setBusy(true);
    // Persist final state + mark setup complete, then land on /wallet.
    const now = new Date().toISOString();
    const w = loadWallet() || {};
    updateWallet({
      language,
      name: name.trim() || "Friend",
      created_at: w.created_at || now,
      accessibility: { ...(w.accessibility || {}), language },
      setup: {
        ...(w.setup || {}),
        language,
        name: name.trim(),
        cell: cell.trim(),
        idnum: idnum.trim(),
        selfie_captured_at: selfie?.captured_at,
        consent: true,
        completed_at: now,
      },
      setup_completed_at: now,
      identity_verified: true,       // simulated in this environment
      identity_verified_at: now,
    });
    setTimeout(() => nav("/wallet"), 500);
  };

  const saveAndExit = () => {
    savePartial();
    nav("/wallet");
  };

  return (
    <div className="min-h-screen" style={{ background: BG }} data-testid="wallet-onboarding">
      {/* NAVI's illustrated intro auto-plays on a fresh device */}
      <NaviIntroModal />

      <div className="max-w-md mx-auto px-4 py-8 lg:py-12">
        <div className="flex items-center gap-3 mb-6">
          <NaviFace size="sm" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: GOLD }}>Setting up your Trust Wallet</div>
            <div className="text-[13px]" style={{ color: NAVY }}><strong>Step {step + 1}</strong> of {total} · about {minutesLeft} min left</div>
          </div>
          {savedFlash && (
            <div className="flex items-center gap-1 text-[11px] font-medium text-emerald-600" data-testid="onboarding-saved-flash">
              <Check className="w-3.5 h-3.5" /> Saved
            </div>
          )}
        </div>

        {/* Progress */}
        <div className="h-1 rounded-full bg-slate-200 overflow-hidden mb-6">
          <div className="h-full transition-all rounded-full" style={{ background: GOLD, width: `${progressPct}%` }} />
        </div>

        <section className="rounded-3xl bg-white shadow-xl border border-slate-100 p-5 lg:p-6" data-testid={`onboarding-step-${cur.key}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
              <cur.icon className="w-5 h-5" style={{ color: NAVY }} />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold" style={{ color: NAVY }}>{cur.title}</h1>
              <p className="text-[12px] text-slate-600 leading-relaxed">{cur.body}</p>
            </div>
          </div>

          <div className="mt-5">
            {cur.key === "language" && (
              <div className="grid grid-cols-2 gap-2">
                {LANGUAGES.map(l => (
                  <button
                    key={l.code}
                    onClick={() => { setLanguage(l.code); persistPatch({ language: l.code }); }}
                    data-testid={`onboarding-lang-${l.code}`}
                    className="px-3 py-2.5 rounded-xl border text-[13px] transition text-left"
                    style={{
                      background: language === l.code ? NAVY : "white",
                      color: language === l.code ? "white" : "#334155",
                      borderColor: language === l.code ? NAVY : "#E2E8F0",
                    }}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}

            {cur.key === "name" && (
              <input
                type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Nomsa"
                data-testid="onboarding-name-input"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-base focus:outline-none focus:border-slate-400"
              />
            )}

            {cur.key === "cell" && (
              <>
                <input
                  type="tel" inputMode="tel" value={cell} onChange={e => setCell(e.target.value)}
                  placeholder="e.g. 082 123 4567"
                  data-testid="onboarding-cell-input"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-base focus:outline-none focus:border-slate-400"
                />
                {cell && !isValidSaCell(cell) && (
                  <div className="text-[11px] text-amber-700 mt-1">That doesn't look like a South African mobile number.</div>
                )}
              </>
            )}

            {cur.key === "idnum" && (
              <>
                <input
                  type="text" inputMode="text" value={idnum} onChange={e => setIdnum(e.target.value)}
                  placeholder="13-digit SA ID or passport number"
                  data-testid="onboarding-idnum-input"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-base focus:outline-none focus:border-slate-400 tracking-wider font-mono"
                />
                {idnum && !isValidSaIdOrPassport(idnum) && (
                  <div className="text-[11px] text-amber-700 mt-1">That doesn't match a valid SA ID or passport format.</div>
                )}
                <div className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                  We store an anonymised match — never your raw ID number.
                </div>
              </>
            )}

            {cur.key === "selfie" && (
              <SelfieCapture
                captured={selfie}
                onCapture={() => {
                  const captured_at = new Date().toISOString();
                  setSelfie({ captured_at });
                  persistPatch({ selfie_captured_at: captured_at });
                }}
                onReset={() => { setSelfie(null); persistPatch({ selfie_captured_at: null }); }}
              />
            )}

            {cur.key === "consent" && (
              <div className="space-y-3">
                <ConsentItem checked>Only I decide when a document is shared with a bank.</ConsentItem>
                <ConsentItem checked>Nothing leaves this device without an explicit tap.</ConsentItem>
                <ConsentItem checked>NAVI can only <em>explain</em> — never advise.</ConsentItem>
                <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
                  <input
                    type="checkbox" checked={consent} onChange={e => { setConsent(e.target.checked); persistPatch({ consent: e.target.checked }); }}
                    data-testid="onboarding-consent-check"
                    className="mt-1"
                  />
                  <span className="text-[13px] text-slate-700 leading-relaxed">
                    I understand the wallet is a private space for <strong>me</strong>. I am ready to create my Trust Wallet.
                  </span>
                </label>
              </div>
            )}
          </div>
        </section>

        {/* Navigation row */}
        <div className="mt-5 flex items-center gap-3">
          {step > 0 && (
            <button
              onClick={() => setStep(s => Math.max(0, s - 1))}
              data-testid="onboarding-back"
              className="inline-flex items-center gap-1 px-3 py-2.5 rounded-xl border border-slate-200 text-[13px] text-slate-700 hover:bg-white transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
          )}

          {step >= 1 && step < total - 1 && (
            <button
              onClick={saveAndExit}
              data-testid="onboarding-save-exit"
              className="inline-flex items-center gap-1 px-3 py-2.5 rounded-xl border border-slate-200 text-[13px] text-slate-700 hover:bg-white transition"
            >
              <Save className="w-3.5 h-3.5" /> Save & exit
            </button>
          )}

          <div className="flex-1" />

          {step < total - 1 ? (
            <button
              onClick={advance}
              disabled={!canContinue}
              data-testid="onboarding-next"
              className="inline-flex items-center gap-1 px-4 py-2.5 rounded-xl font-semibold text-[13px] text-white disabled:opacity-40 transition hover:opacity-95"
              style={{ background: NAVY }}
            >
              Continue <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={finish}
              disabled={!canContinue || busy}
              data-testid="onboarding-finish"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-[13px] text-white disabled:opacity-40 transition hover:opacity-95"
              style={{ background: NAVY }}
            >
              {busy ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating…</>) : "Create my Trust Wallet"}
            </button>
          )}
        </div>

        <p className="text-[10px] text-slate-400 text-center mt-4 leading-relaxed">
          Identity verification is simulated in this preview environment — production uses Smile ID + FSCA-recognised biometric providers.
        </p>
      </div>
    </div>
  );
}

function ConsentItem({ children, checked }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl bg-slate-50 border border-slate-100 p-3">
      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: checked ? GOLD : "#E2E8F0" }}>
        <Check className="w-3 h-3" style={{ color: NAVY }} />
      </div>
      <span className="text-[12.5px] text-slate-700 leading-relaxed">{children}</span>
    </div>
  );
}

function SelfieCapture({ captured, onCapture, onReset }) {
  const [scanning, setScanning] = useState(false);
  const start = () => {
    setScanning(true);
    // Simulated 1.6s "biometric match" so the moment feels real.
    setTimeout(() => { setScanning(false); onCapture(); }, 1600);
  };
  if (captured?.captured_at) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="onboarding-selfie-captured">
        <div className="w-11 h-11 rounded-full bg-emerald-500/15 flex items-center justify-center">
          <Check className="w-5 h-5 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-emerald-900">Face-match verified</div>
          <div className="text-[11px] text-emerald-800">Captured {new Date(captured.captured_at).toLocaleTimeString()}</div>
        </div>
        <button onClick={onReset} className="text-[11px] text-emerald-800 underline" data-testid="onboarding-selfie-retry">Retake</button>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-center" data-testid="onboarding-selfie-capture">
      <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ background: `${NAVY}0F` }}>
        {scanning ? <Loader2 className="w-6 h-6 animate-spin" style={{ color: NAVY }} /> : <Camera className="w-6 h-6" style={{ color: NAVY }} />}
      </div>
      <div className="text-[13px] font-semibold mt-3" style={{ color: NAVY }}>
        {scanning ? "Matching your face to your ID…" : "Tap to take a quick selfie"}
      </div>
      <div className="text-[11px] text-slate-500 mt-1">Only the match result is stored — never your photo.</div>
      <button
        onClick={start}
        disabled={scanning}
        data-testid="onboarding-selfie-start"
        className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-semibold text-white disabled:opacity-40"
        style={{ background: NAVY }}
      >
        {scanning ? "Scanning…" : "Capture selfie"}
      </button>
    </div>
  );
}
